import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { articles, type Insight } from "@/data/marketInsights";
import fallbackImage from "@/assets/nj-luxury-home.webp";

export const BLOG_FALLBACK_IMAGE = fallbackImage;

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string; // ISO
  image: string;
  categories: string[];
  source: "john" | "kcm";
  contentHtml?: string;
  body?: Insight["body"];
  readMinutes?: number;
  originalUrl?: string;
  pullQuote?: string;
}

interface FeedItem {
  title: string;
  link: string;
  imageUrl: string | null;
  excerpt: string;
  category: string;
  categories?: string[];
  pubDate: string;
  slug?: string;
  contentHtml?: string;
}

const johnPosts: BlogPost[] = articles.map((a) => ({
  slug: a.slug,
  title: a.title,
  excerpt: a.excerpt,
  date: a.date,
  image: a.image ?? fallbackImage,
  categories: [a.category],
  source: "john",
  body: a.body,
  readMinutes: a.readMinutes,
  pullQuote: a.pullQuote,
}));

const fetchFeed = async (): Promise<FeedItem[]> => {
  const { data, error } = await supabase.functions.invoke("market-feed");
  if (error) throw error;
  return (data?.items ?? []) as FeedItem[];
};

const toPost = (i: FeedItem): BlogPost | null => {
  const slug =
    i.slug || i.link.split("?")[0].split("/").filter(Boolean).pop() || "";
  if (!slug) return null;
  const text = (i.contentHtml ?? "").replace(/<[^>]+>/g, " ").split(/\s+/).length;
  return {
    slug,
    title: i.title,
    excerpt: i.excerpt,
    date: i.pubDate,
    image: i.imageUrl || fallbackImage,
    categories: i.categories?.length ? i.categories : [i.category || "Market Update"],
    source: "kcm",
    contentHtml: i.contentHtml,
    readMinutes: Math.max(2, Math.round(text / 220)),
    originalUrl: i.link,
  };
};

export const useBlogPosts = () => {
  const query = useQuery({
    queryKey: ["market-feed"],
    queryFn: fetchFeed,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    retry: 1,
  });

  const posts = useMemo(() => {
    const seen = new Set<string>();
    const all: BlogPost[] = [];
    for (const p of [...johnPosts, ...((query.data ?? []).map(toPost).filter(Boolean) as BlogPost[])]) {
      if (seen.has(p.slug)) continue;
      seen.add(p.slug);
      all.push(p);
    }
    return all.sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [query.data]);

  return { posts, isLoading: query.isLoading, isError: query.isError };
};

/** Hook: look up a single post by slug (John's posts resolve instantly). */
export const getBlogPost = (slug: string | undefined) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { posts, isLoading, isError } = useBlogPosts();
  const post = slug ? posts.find((p) => p.slug === slug) : undefined;
  return { post, posts, isLoading, isError };
};

export const formatBlogDate = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(+d)) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};
